/**
 * ProtectedRoute Component
 * 
 * Wrapper component that protects routes requiring authentication.
 * Checks auth state and user capabilities before rendering children.
 * 
 * Requirements: 4.4
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredCapability?: string;
}

/**
 * ProtectedRoute - Guards routes that require authentication
 * 
 * - Redirects to login if not authenticated
 * - Shows permission error if user lacks required capability
 * - Renders children if all checks pass
 */
export function ProtectedRoute({ 
  children, 
  requiredCapability = 'edit_products' 
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasEditProductsCapability, tokenPayload } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="auth-loading">
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for required capability (default: edit_products)
  if (requiredCapability === 'edit_products' && !hasEditProductsCapability) {
    return (
      <div className="permission-error">
        <h2>Permission Denied</h2>
        <p>You don't have permission to add products.</p>
        <p>Please contact your administrator to request the necessary permissions.</p>
      </div>
    );
  }

  // Check for custom capability if specified
  if (requiredCapability !== 'edit_products' && tokenPayload) {
    const capabilities = tokenPayload.data?.user?.capabilities || [];
    if (!capabilities.includes(requiredCapability)) {
      return (
        <div className="permission-error">
          <h2>Permission Denied</h2>
          <p>You don't have the required permission: {requiredCapability}</p>
        </div>
      );
    }
  }

  // All checks passed, render children
  return <>{children}</>;
}

export default ProtectedRoute;
